import { getBookingQuestions } from "helpers/API Calls/System & Settings/Booking Questions";
import { useEffect, useState } from "react";
import { Input, Label } from "reactstrap";
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { selectAccessibilityOptions, setAccessibilityOptions, setBookingNotes } from 'slices/booking/bookingSlice';
import { useSelector } from "react-redux";
import { selectIsDiscountedBooking, selectIsEdit } from "slices/booking/cartSlice";

export interface BookingQuestion {
  id: number;
  title: string;
  hint: string | null;
  is_required: number;
  sequence: number;
  question_type: {
    id: number;
    question_type: string;
    question_type_slug: string;
  };
  options: Array<{
    id: number;
    option: string;
    charges: number;
  }>;
}

interface FormValues {
  [key: number]: string | string[];
}

interface FormErrors {
  [key: number]: string;
}

interface AccessibilityOption {
  question_id: number;
  option_id: number | null;
  free_text: string | null;
  charges: number;
}

function BookingQuestions({ bookingQuestions }: { bookingQuestions: BookingQuestion[] }) {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const isEdit = useSelector(selectIsEdit);
  const bookingQuestionsData = useSelector(selectAccessibilityOptions);
  const isDiscountedBooking = useSelector(selectIsDiscountedBooking);  

  useEffect(() => {
      if (bookingQuestions.length === 0) return;{
        const initialFormValues: FormValues = {};
        bookingQuestions.forEach((question: BookingQuestion) => {
          isEdit && bookingQuestionsData.length > 0
          ? 
          initialFormValues[question.id] = bookingQuestionsData.find(opt => opt.question_id === question.id)?.option_id?.toString() || ''
          :
          initialFormValues[question.id] = question.question_type.question_type_slug === 'text_input' ? '' : '';
        });
        setFormValues(initialFormValues);
      }
  }, [isEdit]);

  const updateReduxState = (updatedValues: FormValues) => {
    const accessibilityOptions: AccessibilityOption[] = bookingQuestions.map(question => {
      const currentValue = updatedValues[question.id];
      const option = question.question_type.question_type_slug === 'multi_select_input' 
        ? question.options.find(opt => opt.id === Number(currentValue))
        : null;
      
      return {
        question_id: question.id,
        option_id: option?.id || null,
        free_text: question.question_type.question_type_slug === 'text_input' 
          ? currentValue?.toString() || null
          : null,
        charges: option?.charges || 0
      };
    });

    // Update accessibility options in Redux
    dispatch(setAccessibilityOptions(accessibilityOptions));

    // Collect and update booking notes
    const textNotes = bookingQuestions
      .filter(q => q.question_type.question_type_slug === 'text_input')
      .map(q => ({
        question: q.title,
        answer: updatedValues[q.id]
      }))
      .filter(item => item.answer)
      .map(item => `${item.question}: ${item.answer}`)
      .join('\n');

    dispatch(setBookingNotes(textNotes));
  };

  const handleInputChange = (questionId: number, value: string | string[]) => {
    const newValues = {
      ...formValues,
      [questionId]: value
    };
    
    setFormValues(newValues);
    
    if (formErrors[questionId]) {
      setFormErrors(prev => ({
        ...prev,
        [questionId]: ''
      }));
    }

    updateReduxState(newValues);
  };

  console.log(bookingQuestions)

  const renderQuestion = (question: BookingQuestion) => {
    switch (question.question_type.question_type_slug) {
      case 'text_input':
        return (
          <div className="form-group mb-3" key={question.id}>
            <Label>
              {question.title}
              {question.is_required === 1 && <span className="text-danger ms-1">*</span>}
              {question.hint && <div className="text-muted small">{question.hint}</div>}
            </Label>
            <Input
              type="text"
              value={formValues[question.id]?.toString() || ''}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              className={formErrors[question.id] ? 'is-invalid' : ''}
              placeholder="Enter your answer"
            />
            {formErrors[question.id] && (
              <div className="invalid-feedback">
                {formErrors[question.id]}
              </div>
            )}
          </div>
        );

      case 'multi_select_input':
        return (
          <div className="form-group mb-3" key={question.id}>
            <Label>
              {question.title}
              {question.is_required === 1 && <span className="text-danger ms-1">*</span>}
              {question.hint && <div className="text-muted small">{question.hint}</div>}
            </Label>
            <Select
              options={question.options.map(option => ({
                value: option.id,
                label: `${option.option} (£${option.charges})`
              }))}
              isDisabled={isDiscountedBooking}
              value={question.options
                .filter(option => option.id === Number(formValues[question.id]))
                .map(option => ({
                  value: option.id,
                  label: `${option.option} (£${option.charges})`
                }))[0] || null}
              onChange={(option) => handleInputChange(question.id, option ? option.value.toString() : '')}
              placeholder="Select an option"
              classNamePrefix="select"
              className={formErrors[question.id] ? 'is-invalid' : ''}
            />
            {formErrors[question.id] && (
              <div className="invalid-feedback d-block">
                {formErrors[question.id]}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {bookingQuestions.sort((a, b) => a.sequence - b.sequence).map(renderQuestion)}
    </div>
  );
}

export default BookingQuestions;
